import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { CalendarClock, ClipboardCheck, GraduationCap, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

import { PageHeader } from '../../shared/ui/PageHeader';
import { Skeleton } from '../../shared/ui/Skeleton';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import {
  getMonthlyMocksCount,
  getPendingGradingCount,
  getStudentsCount,
  getUpcomingSessionsCount,
  INSTITUTE_QUERY_KEYS,
} from './api';
import { AttendanceBoard } from './AttendanceBoard';
import s from './DashboardPage.module.css';
import { MyWorkPanel } from './MyWorkPanel';
import {
  getInstituteResults,
  INSTITUTE_RESULTS_KEYS,
  type InstituteResultRow,
  type ResultAttemptStatus,
} from './results-api';
import { useCurrentInstitute } from './useCurrentInstitute';

interface KpiCardProps {
  label: string;
  value: number | undefined;
  loading: boolean;
  error: Error | null;
  helper?: string;
  action?: { to: string; label: string };
}

function KpiCard({ label, value, loading, error, helper, action }: KpiCardProps): JSX.Element {
  const body = (
    <>
      <span className={s.statLabel}>{label}</span>
      <span className={s.statValue} aria-live="polite">
        {loading ? (
          <span role="status" aria-label={`Loading ${label.toLowerCase()}`}>
            <Skeleton width={40} height={24} radius="sm" />
          </span>
        ) : error !== null ? (
          <span className={s.statErr} title={error.message}>
            —
          </span>
        ) : (
          (value ?? 0)
        )}
      </span>
      {helper !== undefined ? <span className={s.statHelper}>{helper}</span> : null}
    </>
  );

  if (action !== undefined) {
    return (
      <Link to={action.to} className={s.statLink} aria-label={action.label}>
        {body}
      </Link>
    );
  }
  return (
    <div className={s.stat} aria-busy={loading || undefined}>
      {body}
    </div>
  );
}

const STATUS_LABEL: Record<ResultAttemptStatus, string> = {
  submitted: 'Submitted',
  grading: 'Grading',
  completed: 'Published',
};

/** Three plates the height of a feed row — the list a loaded card shows. */
const FEED_SKELETON_ROWS = [0, 1, 2] as const;

function bandLabel(value: number | null): string {
  return value === null ? '—' : value.toFixed(1);
}

function RecentSubmissions({
  rows,
  loading,
}: {
  rows: InstituteResultRow[];
  loading: boolean;
}): JSX.Element {
  if (loading) {
    return (
      <div className={s.feedSkeleton} role="status" aria-label="Loading recent submissions">
        {FEED_SKELETON_ROWS.map((row) => (
          <Skeleton key={row} height={44} radius="md" />
        ))}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <p className={s.cardBody}>
        No submissions yet. When a student finishes a mock or practice test, it appears here.
      </p>
    );
  }
  return (
    <ul className={s.feed}>
      {rows.map((row) => (
        <li key={row.attempt_id} className={s.feedItem}>
          <Link to={`/institute/results/${row.attempt_id}`} className={s.feedLink}>
            <span className={s.feedMain}>
              <span className={s.feedName}>{row.student_name}</span>
              <span className={s.feedSub}>{row.test_title}</span>
            </span>
            <span className={s.feedRight}>
              <span className={s.feedStatus}>{STATUS_LABEL[row.status]}</span>
              <span className={s.feedBand}>{bandLabel(row.overall_band)}</span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

const QUICK_ACTIONS: readonly { to: string; label: string; hint: string }[] = [
  { to: '/institute/students', label: 'Manage students', hint: 'Invite, import, archive' },
  { to: '/institute/sessions/new', label: 'Schedule a mock', hint: 'Pick a test and a batch' },
  { to: '/institute/grading', label: 'Grading queue', hint: 'Approve AI-marked writing' },
  { to: '/institute/builder', label: 'Test builder', hint: 'Build from PDFs' },
];

export function InstituteDashboardPage(): JSX.Element {
  const current = useCurrentInstitute();
  const instituteId = current.instituteId;
  const enabled = instituteId !== null;

  const studentsQ = useQuery({
    queryKey: enabled
      ? INSTITUTE_QUERY_KEYS.stats.studentsCount(instituteId)
      : ['institute', 'stats', 'students_count', 'disabled'],
    queryFn: () => getStudentsCount(instituteId as string),
    enabled,
  });
  const upcomingQ = useQuery({
    queryKey: enabled
      ? INSTITUTE_QUERY_KEYS.stats.upcomingSessions(instituteId)
      : ['institute', 'stats', 'upcoming_sessions', 'disabled'],
    queryFn: () => getUpcomingSessionsCount(instituteId as string),
    enabled,
  });
  const gradingQ = useQuery({
    queryKey: enabled
      ? INSTITUTE_QUERY_KEYS.stats.pendingGrading(instituteId)
      : ['institute', 'stats', 'pending_grading', 'disabled'],
    queryFn: () => getPendingGradingCount(instituteId as string),
    enabled,
  });
  const mocksQ = useQuery({
    queryKey: enabled
      ? INSTITUTE_QUERY_KEYS.stats.monthlyMocks(instituteId)
      : ['institute', 'stats', 'monthly_mocks', 'disabled'],
    queryFn: () => getMonthlyMocksCount(instituteId as string),
    enabled,
  });
  const recentQ = useQuery({
    queryKey: enabled
      ? INSTITUTE_RESULTS_KEYS.list(instituteId)
      : ['institute', 'results', 'disabled'],
    queryFn: () => getInstituteResults(instituteId as string),
    enabled,
  });
  const recent = (recentQ.data ?? []).slice(0, 5);

  return (
    <div className={s.root}>
      <PageHeader
        eyebrow="Workspace overview"
        title="Institute dashboard"
        actions={
          <span className={s.dateChip}>
            <CalendarClock size={14} aria-hidden="true" />
            {dayjs().format('dddd, D MMMM YYYY')}
          </span>
        }
      />

      {current.error !== null ? (
        <StatusBanner tone="error">
          Could not load your institute: {current.error.message}
        </StatusBanner>
      ) : null}

      {enabled ? (
        <AttendanceBoard instituteId={instituteId} />
      ) : current.isPending ? (
        <div className={s.boardPlaceholder} role="status" aria-label="Loading your workspace">
          <Skeleton height={260} radius="xl" />
        </div>
      ) : null}

      {enabled ? <MyWorkPanel instituteId={instituteId} /> : null}

      <section className={s.stats} aria-label="Snapshot">
        <KpiCard
          label="Students"
          value={studentsQ.data}
          loading={studentsQ.isPending}
          error={studentsQ.error}
          helper="Enrolled"
        />
        <KpiCard
          label="Upcoming sessions"
          value={upcomingQ.data}
          loading={upcomingQ.isPending}
          error={upcomingQ.error}
          helper="Scheduled ahead"
        />
        <KpiCard
          label="Pending grading"
          value={gradingQ.data}
          loading={gradingQ.isPending}
          error={gradingQ.error}
          helper="Awaiting examiner"
          action={{ to: '/institute/grading', label: 'Open the grading queue' }}
        />
        <KpiCard
          label="Mocks this month"
          value={mocksQ.data}
          loading={mocksQ.isPending}
          error={mocksQ.error}
          helper="Since the 1st"
        />
      </section>

      <section className={s.grid}>
        <article className={s.card}>
          <div className={s.cardHead}>
            <h2 className={s.cardTitle}>Recent submissions</h2>
            <Link to="/institute/results" className={s.cardHeadLink}>
              View all →
            </Link>
          </div>
          <RecentSubmissions rows={recent} loading={!enabled || recentQ.isPending} />
        </article>

        <article className={s.card}>
          <h2 className={s.cardTitle}>Quick actions</h2>
          <ul className={s.actions}>
            {QUICK_ACTIONS.map((action) => (
              <li key={action.to}>
                <Link to={action.to} className={s.actionLink}>
                  <span className={s.actionIcon} aria-hidden="true">
                    {action.to.includes('students') ? (
                      <Users size={16} />
                    ) : action.to.includes('sessions') ? (
                      <CalendarClock size={16} />
                    ) : action.to.includes('grading') ? (
                      <ClipboardCheck size={16} />
                    ) : (
                      <GraduationCap size={16} />
                    )}
                  </span>
                  <span className={s.actionText}>
                    <span className={s.actionLabel}>{action.label}</span>
                    <span className={s.actionHint}>{action.hint}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </article>
      </section>
    </div>
  );
}
